import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Zap, Save, X, RefreshCw, Eye, EyeOff, KeyRound, AlertCircle, CheckCircle2, ChevronLeft } from "lucide-react";
import BrandLogo from "../components/BrandLogo";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const axiosAuth = () => {
  const token = localStorage.getItem("lf_session");
  return axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
};

const CATEGORY_ORDER = ["Payments", "Email", "AI", "Analytics", "Auth"];

export default function AdminSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState([]);
  const [editing, setEditing] = useState(null); // {key, label, ...}
  const [editValue, setEditValue] = useState("");
  const [showValue, setShowValue] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [testResults, setTestResults] = useState({}); // key -> {ok, message}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axiosAuth().get("/admin/settings");
      setSettings(data.settings || []);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/admin/login");
      else toast.error("Could not load settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const grouped = CATEGORY_ORDER
    .map((cat) => ({ category: cat, items: settings.filter((s) => s.category === cat) }))
    .filter((g) => g.items.length > 0);

  const onSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await axiosAuth().put(`/admin/settings/${editing.key}`, { value: editValue });
      toast.success(`Saved ${editing.label}`);
      setEditing(null);
      setEditValue("");
      setShowValue(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onClear = async (item) => {
    if (!window.confirm(`Clear stored value for "${item.label}"? It will fall back to the environment variable (if any).`)) return;
    try {
      await axiosAuth().delete(`/admin/settings/${item.key}`);
      toast.success(`Cleared ${item.label}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clear failed");
    }
  };

  const onTest = async (item, valueOverride = null) => {
    setTesting(item.key);
    try {
      const body = valueOverride !== null ? { value: valueOverride } : {};
      const { data } = await axiosAuth().post(`/admin/settings/${item.key}/test`, body);
      setTestResults((r) => ({ ...r, [item.key]: data }));
      if (data.ok) toast.success(`✓ ${item.label}: ${data.message}`);
      else toast.error(`${item.label}: ${data.message}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Test failed";
      setTestResults((r) => ({ ...r, [item.key]: { ok: false, message: msg } }));
      toast.error(msg);
    } finally {
      setTesting(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-settings-page">
      <header className="bg-white border-b border-zinc-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            data-testid="btn-back-dashboard"
            onClick={() => navigate("/admin/dashboard")}
            className="p-1.5 rounded-lg hover:bg-zinc-100"
            aria-label="Back to dashboard"
          >
            <ChevronLeft size={18} />
          </button>
          <BrandLogo variant="mark" size={32} />
          <div>
            <div className="font-bold text-zinc-900">API Keys & Integrations</div>
            <div className="text-xs text-zinc-500">Encrypted at rest. Values override the environment defaults.</div>
          </div>
        </div>
        <button
          data-testid="btn-refresh-settings"
          onClick={load}
          className="flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900 border border-zinc-200 px-3 py-2 rounded-lg hover:bg-zinc-50"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        {grouped.map((group) => (
          <section
            key={group.category}
            className="bg-white border border-zinc-200 rounded-2xl overflow-hidden"
            data-testid={`settings-group-${group.category.toLowerCase()}`}
          >
            <div className="px-6 py-4 border-b border-zinc-100 flex items-center gap-2">
              <KeyRound size={14} className="text-zinc-500" />
              <h2 className="font-semibold text-zinc-900">{group.category}</h2>
              <span className="text-xs text-zinc-500 ml-auto">{group.items.length} keys</span>
            </div>
            <div className="divide-y divide-zinc-100">
              {group.items.map((item) => {
                const tr = testResults[item.key];
                return (
                  <div key={item.key} className="px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`settings-row-${item.key}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-zinc-900 text-sm">{item.label}</span>
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded ${
                            item.source === "db"
                              ? "bg-blue-50 text-blue-700"
                              : item.source === "env"
                              ? "bg-zinc-100 text-zinc-600"
                              : "bg-amber-50 text-amber-700"
                          }`}
                          data-testid={`settings-source-${item.key}`}
                        >
                          {item.source === "db" ? "Saved (DB)" : item.source === "env" ? "Env default" : "Not set"}
                        </span>
                        {item.is_secret && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600">Secret</span>
                        )}
                      </div>
                      <div className="text-xs text-zinc-500 mt-1">{item.description}</div>
                      <div className="text-xs font-mono text-zinc-700 mt-2 truncate" data-testid={`settings-preview-${item.key}`}>
                        {item.preview || <span className="text-zinc-400">— not configured —</span>}
                      </div>
                      {tr && (
                        <div
                          className={`mt-2 inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded ${
                            tr.ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
                          }`}
                          data-testid={`settings-test-result-${item.key}`}
                        >
                          {tr.ok ? <CheckCircle2 size={12} /> : <AlertCircle size={12} />}
                          <span className="truncate max-w-[420px]">{tr.message}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {item.tester && (
                        <button
                          data-testid={`btn-test-${item.key}`}
                          onClick={() => onTest(item)}
                          disabled={testing === item.key}
                          className="text-xs px-3 py-1.5 rounded-md border border-zinc-200 text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
                        >
                          {testing === item.key ? "Testing…" : "Test"}
                        </button>
                      )}
                      <button
                        data-testid={`btn-edit-${item.key}`}
                        onClick={() => {
                          setEditing(item);
                          setEditValue("");
                          setShowValue(!item.is_secret);
                        }}
                        className="text-xs px-3 py-1.5 rounded-md bg-zinc-950 text-white hover:bg-zinc-800"
                      >
                        Edit
                      </button>
                      {item.source === "db" && (
                        <button
                          data-testid={`btn-clear-${item.key}`}
                          onClick={() => onClear(item)}
                          className="text-xs px-3 py-1.5 rounded-md border border-red-200 text-red-700 hover:bg-red-50"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div
            className="bg-white rounded-2xl border border-zinc-200 max-w-lg w-full p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="settings-edit-modal"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-zinc-900">{editing.label}</h3>
              <button onClick={() => setEditing(null)} aria-label="Close" data-testid="btn-close-edit-modal">
                <X size={18} />
              </button>
            </div>
            <p className="text-xs text-zinc-500 mb-4">{editing.description}</p>
            <label className="block text-xs font-semibold text-zinc-700 mb-2">New value</label>
            <div className="relative">
              <input
                data-testid="input-setting-value"
                type={editing.is_secret && !showValue ? "password" : "text"}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                placeholder={editing.preview || "Enter value…"}
                className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm font-mono pr-10"
                autoFocus
              />
              {editing.is_secret && (
                <button
                  type="button"
                  onClick={() => setShowValue((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-zinc-400 hover:text-zinc-700"
                  aria-label={showValue ? "Hide value" : "Show value"}
                  data-testid="btn-toggle-show-value"
                >
                  {showValue ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 mt-5">
              {editing.tester && (
                <button
                  data-testid="btn-test-before-save"
                  onClick={() => onTest(editing, editValue)}
                  disabled={!editValue || testing === editing.key}
                  className="text-xs px-3 py-2 rounded-md border border-zinc-200 text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
                >
                  {testing === editing.key ? "Testing…" : "Test before saving"}
                </button>
              )}
              <button
                data-testid="btn-save-setting"
                onClick={onSave}
                disabled={!editValue || saving}
                className="flex items-center gap-1.5 text-xs px-4 py-2 rounded-md bg-zinc-950 text-white hover:bg-zinc-800 disabled:opacity-50"
              >
                <Save size={12} /> {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
